import React, { useEffect, useState } from 'react';
import api from './api';
import ProjectModal from './ProjectModal';
import { PROJECT_STATUS_BADGE_CLASS, PROJECT_STATUS_LABELS, PROJECT_PRIORITIES, PROJECT_PRIORITY_LABELS, PROJECT_PRIORITY_BADGE_CLASS, projectCode, userDisplayName, formatDate } from './helpers';

// « Archivés » n'est pas un statut mais une vue à part : le serveur ne renvoie
// les projets archivés que si on les demande explicitement (?archived=1).
const FILTERS = ['Tous', 'Active', 'Delayed', 'Completed', 'Cancelled', 'Archived'];
const FILTER_LABELS = { Tous: 'Tous', Active: 'Actifs', Delayed: 'En retard', Completed: 'Terminés', Cancelled: 'Annulés', Archived: 'Archivés' };
const SORTS = ['Aucun', 'Priorité'];

function Projects({ onSelectProject, users, searchQuery }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('Tous');
  const [priorityFilter, setPriorityFilter] = useState('Toutes');
  const [sortBy, setSortBy] = useState('Aucun');
  const [showModal, setShowModal] = useState(false);
  const [view, setView] = useState('cards'); // 'cards' | 'summary'

  const load = React.useCallback(() => {
    setLoading(true);
    api
      .get(filter === 'Archived' ? '/projects?archived=1' : '/projects')
      .then((res) => setProjects(res.data))
      .catch(() => setError('Impossible de charger les projets.'))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const query = (searchQuery || '').trim().toLowerCase();

  let filtered =
    filter === 'Tous' || filter === 'Archived'
      ? projects
      : projects.filter((p) => p.status === filter);
  if (priorityFilter !== 'Toutes') {
    filtered = filtered.filter((p) => p.priority === priorityFilter);
  }
  if (query) {
    filtered = filtered.filter((p) => p.name.toLowerCase().includes(query) || projectCode(p.id).toLowerCase().includes(query) || (p.description || '').toLowerCase().includes(query));
  }
  if (sortBy === 'Priorité') {
    filtered = [...filtered].sort((a, b) => (a.priority || 'Z').localeCompare(b.priority || 'Z'));
  }

  const handleSaved = (project) => {
    setShowModal(false);
    load();
  };

  const managerName = (id) => userDisplayName(users.find((u) => u.id === id));
  const budgetPct = (p) => p.budget_allocated ? Math.round(((p.budget_spent || 0) / p.budget_allocated) * 100) : 0;

  if (loading) return <p>Chargement...</p>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="fade-in">
      <div className="page-header-row">
        <div className="filter-pills">
          {FILTERS.map((f) => (
            <button
              key={f}
              className={`filter-pill ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {FILTER_LABELS[f]}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div className="view-toggle">
            <button className={`view-toggle-btn ${view === 'cards' ? 'active' : ''}`} onClick={() => setView('cards')}>Cartes</button>
            <button className={`view-toggle-btn ${view === 'summary' ? 'active' : ''}`} onClick={() => setView('summary')}>Résumé</button>
          </div>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="btn btn-sm">
            <option value="Toutes">Toutes priorités</option>
            {PROJECT_PRIORITIES.map((p) => (
              <option key={p} value={p}>{PROJECT_PRIORITY_LABELS[p]}</option>
            ))}
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="btn btn-sm">
            {SORTS.map((s) => (
              <option key={s} value={s}>{s === 'Aucun' ? 'Tri : aucun' : 'Trier par priorité'}</option>
            ))}
          </select>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Nouveau projet</button>
        </div>
      </div>

      {view === 'cards' && (
        <div className="projects-grid">
          {filtered.map((project) => (
            <div key={project.id} className="project-card" onClick={() => onSelectProject(project)}>
              <div className="project-card-top">
                <span className="project-ref">{projectCode(project.id)}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  {project.priority && (
                    <span className={`badge ${PROJECT_PRIORITY_BADGE_CLASS[project.priority] || 'badge-todo'}`}>
                      {project.priority}
                    </span>
                  )}
                  <span className={`badge ${PROJECT_STATUS_BADGE_CLASS[project.status] || 'badge-active'}`}>
                    {PROJECT_STATUS_LABELS[project.status] || project.status}
                  </span>
                </div>
              </div>
              <div className="project-title">{project.name}</div>
              <div className="project-meta">{project.description}</div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${project.progress_percent}%` }} />
              </div>
              <div className="project-card-footer">
                <span>Avancement</span>
                <span>{project.progress_percent}%</span>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="empty-state">
              {filter === 'Archived' ? 'Aucun projet archivé.' : 'Aucun projet dans cette catégorie.'}
            </div>
          )}
        </div>
      )}

      {view === 'summary' && (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Réf.</th>
                <th>Projet</th>
                <th>Statut</th>
                <th>Priorité</th>
                <th>Avancement</th>
                <th>Budget consommé</th>
                <th>Tâches</th>
                <th>Chef de projet</th>
                <th>Échéance</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="data-table-row" onClick={() => onSelectProject(p)} style={{ cursor: 'pointer' }}>
                  <td>{projectCode(p.id)}</td>
                  <td><div style={{ fontWeight: 600 }}>{p.name}</div></td>
                  <td>
                    <span className={`badge ${PROJECT_STATUS_BADGE_CLASS[p.status] || 'badge-active'}`}>
                      {PROJECT_STATUS_LABELS[p.status] || p.status}
                    </span>
                  </td>
                  <td>
                    {p.priority && (
                      <span className={`badge ${PROJECT_PRIORITY_BADGE_CLASS[p.priority] || 'badge-todo'}`}>{p.priority}</span>
                    )}
                  </td>
                  <td style={{ minWidth: 110 }}>
                    <div className="progress-bar" style={{ margin: '0 0 4px 0' }}>
                      <div className="progress-fill" style={{ width: `${p.progress_percent || 0}%` }} />
                    </div>
                    <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{p.progress_percent || 0}%</span>
                  </td>
                  <td>{budgetPct(p)}%</td>
                  <td>{p.task_done_count ?? 0}/{p.task_count ?? 0}</td>
                  <td>{managerName(p.project_manager_id)}</td>
                  <td>{formatDate(p.end_date)}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="empty-state">Aucun projet dans cette catégorie.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <ProjectModal
          users={users}
          onClose={() => setShowModal(false)}
          onSaved={handleSaved}
          onDeleted={() => { setShowModal(false); load(); }}
        />
      )}
    </div>
  );
}

export default Projects;
